// Import the needed functions and objects from byway-names.js
import { getBywayFullName, getBywayDescription, getBywayWebsite } from './byway-names.js';

// Initialize the map centered on Florida (default before location is available)
const map = L.map('map', {
	zoomControl: false,  // Disable zoom controls for Google Maps-like appearance
	minZoom: 6          // Set minimum zoom level to 6 to limit how far users can zoom out
}).setView([27.8, -83.5], 7);

// Add tile layer (OpenStreetMap)
L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
	attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
	maxZoom: 19
}).addTo(map);

// Log initial zoom level
console.log("Initial map zoom level:", map.getZoom());

// Add zoom change event listener
map.on('zoomend', function () {
	console.log("Map zoom level changed to:", map.getZoom());
});

// Variables to store data
let bywaysData = [];
let routeLayers = {};
let routeMarkers = {};
let counties = new Set();
let bywayNames = new Set();
let byways = new Set();
let userLocation = null;

// Define a color palette with distinct colors instead of random colors
const colorPalette = [
	'#e6194b', '#3cb44b', '#4363d8', '#f58231', '#911eb4', '#f032e6', '#cd6600',
	'#e6beff', '#9a6324', '#800000', '#f44336', '#ff5722', '#ff9800', '#ffd700',
	'#9c27b0', '#673ab7', '#3f51b5', '#2196f3', '#009688', '#ff4081', '#00bcd4',
	'#651fff', '#d50000', '#aa00ff', '#c51162', '#6200ea', '#304ffe', '#0091ea'
];
let colorIndex = 0;
const usedColors = new Set();
const bywayColors = {};

// Get a unique color for a byway
function getUniqueColor() {
	// If we've used all colors, reset to start of palette
	if (colorIndex >= colorPalette.length) {
		colorIndex = 0;
	}

	// Get the next color from the palette
	const color = colorPalette[colorIndex];
	colorIndex++;

	return color;
}

// Replace the random color generator function
function getRandomColor() {
	return getUniqueColor();
}

// Modified fetchData function to handle offline state
async function fetchData() {
	try {
		let response;
		let offlineMode = false;

		try {
			response = await fetch('data/florida.json');
			if (!response.ok) {
				throw new Error(`Byways data request failed with status ${response.status}`);
			}
		} catch (networkError) {
			console.warn('Network request failed, attempting to use cached data');
			offlineMode = true;

			if ('caches' in window) {
				response = await caches.match('data/florida.json');
			}

			if (!response) throw networkError;
		}

		const data = await response.json();
		bywaysData = data.features;

		// Process data
		processData();

		// Display data on the map
		displayBywaysOnMap();

		// Populate full byway list
		populateBywaysList();

		// Attach only needed listeners
		setupEventListeners();

		// Show offline indicator if we're using cached data
		if (offlineMode) {
			showOfflineDataNotification();
		}

	} catch (error) {
		console.error('Error loading data:', error);
		showErrorModal('Failed to load byways data. If you\'re offline, please try again when you have an internet connection.');
	}
}

// Add function to show notification when using cached data
function showOfflineDataNotification() {
	const notification = document.createElement('div');
	notification.className = 'cached-data-notice';
	notification.innerHTML = `
        <i class="fas fa-database"></i>
        You're viewing cached data in offline mode. Some information may not be up to date.
    `;

	// Insert at the top of byways list if it exists
	const bywaysList = document.getElementById('byways-list-items');
	if (bywaysList && bywaysList.firstChild) {
		bywaysList.insertBefore(notification, bywaysList.firstChild);
	} else {
		// Alternative: show as modal
		showNotificationModal(
			'Offline Mode',
			'You\'re currently viewing cached data in offline mode. Some features may be limited and information may not be up to date.'
		);
	}
}

// Process the data to extract needed information
function processData() {
	// First, extract the domain values for all byways
	const scenehwyField = bywaysData[0]?.fields?.find(f => f.name === "SCENEHWY");
	const scenehwyDomain = scenehwyField?.domain?.codedValues;

	// We need to extract data from the features too
	bywaysData.forEach(feature => {
		const attr = feature.attributes;

		// Extract counties
		if (attr.COUNTY) {
			counties.add(attr.COUNTY);
		}

		// Extract byway codes
		if (attr.SCENEHWY) {
			byways.add(attr.SCENEHWY);
		}
	});

	// Pre-assign a unique color to each byway code
	Array.from(byways).forEach(bywayCode => {
		if (!bywayColors[bywayCode]) {
			bywayColors[bywayCode] = getUniqueColor();
		}
	});

	// Add byway names from the domain if available
	if (scenehwyDomain) {
		scenehwyDomain.forEach(byway => {
			bywayNames.add(byway.name);

			// Make sure every code has a color assigned
			if (!bywayColors[byway.code]) {
				bywayColors[byway.code] = getRandomColor();
			}
		});
	} else {
		// If we don't have the domain, use DESCRIPT as fallback
		bywaysData.forEach(feature => {
			const attr = feature.attributes;
			if (attr.DESCRIPT) {
				bywayNames.add(attr.DESCRIPT);
			}
		});
	}
}

// Modify displayBywaysOnMap to handle offline map tiles
function displayBywaysOnMap() {
	// Handle case where map tiles might not load in offline mode
	map.on('tileerror', function (e) {
		console.warn('Tile error occurred, may be offline');

		// Only show placeholder if we haven't already
		if (!document.querySelector('.offline-map-placeholder')) {
			const mapContainer = map.getContainer();
			const placeholder = document.createElement('div');
			placeholder.className = 'offline-map-placeholder';
			placeholder.innerHTML = `
                <i class="fas fa-map-marked-alt"></i>
                <h3>Map tiles unavailable offline</h3>
                <p>You appear to be offline and map tiles haven't been cached.
                   Byway data is still available in the list view.</p>
                <button class="btn btn-primary" id="view-byways-list">View Byways List</button>
            `;

			// Insert after the map container
			mapContainer.parentNode.insertBefore(placeholder, mapContainer.nextSibling);

			// Hide the actual map
			mapContainer.style.display = 'none';

			// Add click handler for the button
			document.getElementById('view-byways-list').addEventListener('click', () => {
				const bywaysButton = document.querySelector('.category[data-category="byways"]');
				if (bywaysButton) {
					bywaysButton.click();
				}
			});
		}
	});

	// Continue with existing byways display logic
	bywaysData.forEach(feature => {
		const attr = feature.attributes;
		const geometry = feature.geometry;

		if (geometry && geometry.paths) {
			// Convert ESRI paths to Leaflet format (they're reversed)
			const latlngs = geometry.paths.map(path =>
				path.map(coord => [coord[1], coord[0]])
			);

			// First, create an invisible wider polyline for better touch/click area
			const hitboxPolyline = L.polyline(latlngs, {
				color: bywayColors[attr.SCENEHWY],
				weight: 24,          // Much wider for easier tapping/clicking
				opacity: 0,          // Completely transparent
				className: 'route-path-hitbox'
			}).addTo(map);

			// Then create the visible polyline for the byway
			const visiblePolyline = L.polyline(latlngs, {
				color: bywayColors[attr.SCENEHWY],
				weight: 4,
				opacity: 0.8,
				className: 'route-path'
			}).addTo(map);

			// Find the full name
			let fullName = attr.DESCRIPT || attr.SCENEHWY;

			// Store the layers for later reference
			if (!routeLayers[attr.SCENEHWY]) {
				routeLayers[attr.SCENEHWY] = [];
			}
			routeLayers[attr.SCENEHWY].push(visiblePolyline);

			// Store hitbox polylines separately if needed
			if (!window.hitboxLayers) {
				window.hitboxLayers = {};
			}
			if (!window.hitboxLayers[attr.SCENEHWY]) {
				window.hitboxLayers[attr.SCENEHWY] = [];
			}
			window.hitboxLayers[attr.SCENEHWY].push(hitboxPolyline);

			// Add click event to the visible polyline with stopPropagation
			visiblePolyline.on('click', (e) => {
				L.DomEvent.stopPropagation(e);
				showBywayDetails(attr, fullName);
				highlightByway(attr.SCENEHWY);
			});

			// Add click event to the hitbox polyline for better touch area with stopPropagation
			hitboxPolyline.on('click', (e) => {
				L.DomEvent.stopPropagation(e);
				showBywayDetails(attr, fullName);
				highlightByway(attr.SCENEHWY);
			});

			// Store start and end points for this route segment
			if (!routeMarkers[attr.SCENEHWY]) {
				routeMarkers[attr.SCENEHWY] = [];
			}

			// Store the start and end points of this segment
			if (latlngs.length > 0 && latlngs[0].length > 0) {
				const startPoint = latlngs[0][0];
				const endPoint = latlngs[0][latlngs[0].length - 1];

				routeMarkers[attr.SCENEHWY].push({
					start: startPoint,
					end: endPoint,
					fullName: fullName
				});
			}
		}
	});
}

// Add start and end markers for a selected route - revised to show only one marker
function addRouteMarkers(routeCode, latlngs, fullName) {
	// Check if we have enough data to proceed
	if (!routeMarkers[routeCode] || routeMarkers[routeCode].length === 0) {
		console.error(`No route markers found for byway code ${routeCode}`);
		return;
	}

	// First, clear any existing markers
	clearRouteMarkers();

	// Create a map to store unique points (to avoid duplicate markers)
	const uniquePoints = new Map();

	// Get all start and end points for this route
	routeMarkers[routeCode].forEach(points => {
		// Create unique keys for the points
		const startKey = `${points.start[0]}-${points.start[1]}`;
		const endKey = `${points.end[0]}-${points.end[1]}`;

		if (!uniquePoints.has(startKey)) {
			uniquePoints.set(startKey, {
				point: points.start,
				isStart: true,
				name: points.fullName || fullName,
				distance: userLocation ? calculateDistance(userLocation, points.start) : Infinity
			});
		}

		if (!uniquePoints.has(endKey)) {
			uniquePoints.set(endKey, {
				point: points.end,
				isStart: false,
				name: points.fullName || fullName,
				distance: userLocation ? calculateDistance(userLocation, points.end) : Infinity
			});
		}
	});

	// Convert to array to sort and find closest point
	const pointsArray = Array.from(uniquePoints.values());

	// Choose the point to display
	let pointToShow;

	if (userLocation) {
		// If we have user location, find the closest point
		pointToShow = pointsArray.sort((a, b) => a.distance - b.distance)[0];
	} else {
		// If no user location, prioritize start points
		const startPoints = pointsArray.filter(p => p.isStart);
		pointToShow = startPoints.length > 0 ? startPoints[0] : pointsArray[0];
	}

	// Only create a marker for the selected point
	if (pointToShow) {
		const markerType = pointToShow.isStart ? 'start' : 'end';
		const icon = pointToShow.isStart ?
			'<i class="fas fa-play-circle"></i>' :
			'<i class="fas fa-flag-checkered"></i>';

		const marker = L.marker(pointToShow.point, {
			icon: L.divIcon({
				className: `route-marker ${markerType}-marker`,
				html: icon,
				iconSize: [30, 30],
				iconAnchor: [15, 15]
			})
		}).addTo(map);

		// Determine a descriptive title
		const title = `${pointToShow.isStart ? 'Start' : 'End'} of ${pointToShow.name}`;

		// Create popup with navigation options
		const popup = createNavigationPopup(pointToShow.point, title);
		marker.bindPopup(popup).openPopup();

		// Store reference to remove later
		window.currentMarkers = window.currentMarkers || [];
		window.currentMarkers.push(marker);

		// Highlight the selected byway
		highlightByway(routeCode);
	}
}

// Clear any markers currently on the map
function clearRouteMarkers() {
	if (window.currentMarkers && window.currentMarkers.length) {
		window.currentMarkers.forEach(marker => {
			map.removeLayer(marker);
		});
		window.currentMarkers = [];
	}
}

// Get the user's location
function getUserLocation() {
	if (navigator.geolocation) {
		// Check if we're on a secure context (HTTPS)
		const isSecureContext = window.isSecureContext;

		if (!isSecureContext) {
			console.warn("Geolocation requires HTTPS. Current location features will not work.");
			showErrorModal("Location services require HTTPS. Please access this site via HTTPS to use location features.");
			return;
		}

		navigator.geolocation.getCurrentPosition(
			// Success callback
			(position) => {
				userLocation = {
					lat: position.coords.latitude,
					lng: position.coords.longitude
				};

				// Center map on user's location with appropriate zoom
				map.setView([userLocation.lat, userLocation.lng], 8);

				// Add a marker for the user's location
				if (window.userLocationMarker) {
					map.removeLayer(window.userLocationMarker);
				}

				window.userLocationMarker = L.marker([userLocation.lat, userLocation.lng], {
					icon: L.divIcon({
						className: 'user-location-marker',
						html: '<i class="fas fa-circle"></i>',
						iconSize: [18, 18],
						iconAnchor: [9, 9]
					})
				}).addTo(map);

				// Add pulsing effect to the marker
				const userMarkerElement = window.userLocationMarker.getElement();
				if (userMarkerElement) {
					userMarkerElement.classList.add('pulsing');
				}

				// Update the UI to indicate location is active
				const locationBtn = document.querySelector('.location-button');
				if (locationBtn) {
					locationBtn.classList.remove('locating');
					locationBtn.classList.add('active');
				}

				// Find nearby byways if data is already loaded
				if (bywaysData.length > 0) {
					findNearbyByways();
				}
			},
			// Error callback
			(error) => {
				console.error("Error getting user location:", error);

				// Show appropriate error message
				let errorMsg = "Could not determine your location.";

				switch (error.code) {
					case error.PERMISSION_DENIED:
						errorMsg = "Location access was denied. Please enable location services to find nearby byways.";
						break;
					case error.POSITION_UNAVAILABLE:
						errorMsg = "Location information is unavailable. Please try again later.";
						break;
					case error.TIMEOUT:
						errorMsg = "Location request timed out. Please try again.";
						break;
				}

				showErrorModal(errorMsg);

				// Remove locating class if it was added
				const locationBtn = document.querySelector('.location-button');
				if (locationBtn) {
					locationBtn.classList.remove('locating');
				}
			},
			// Options
			{
				enableHighAccuracy: true,
				timeout: 10000,
				maximumAge: 30000
			}
		);

		// Show "locating" indicator
		const locationBtn = document.querySelector('.location-button');
		if (locationBtn) {
			locationBtn.classList.add('locating');
		}
	} else {
		showErrorModal("Geolocation is not supported by this browser.");
	}
}

// Find byways near the user's current location
function findNearbyByways() {
	if (!userLocation || !bywaysData.length) return;

	const nearbyDistance = 50; // km
	let nearestByway = null;
	let minDistance = Infinity;

	// Check each byway for proximity
	Object.keys(routeMarkers).forEach(code => {
		routeMarkers[code].forEach(segment => {
			const startDistance = calculateDistance(userLocation, segment.start);
			const endDistance = calculateDistance(userLocation, segment.end);

			const minSegmentDistance = Math.min(startDistance, endDistance);

			if (minSegmentDistance < minDistance) {
				minDistance = minSegmentDistance;
				nearestByway = code;
			}
		});
	});

	// If we found a nearby byway (within threshold), highlight it
	if (nearestByway && minDistance <= nearbyDistance) {
		const bywayName = getBywayFullName(nearestByway);

		try {
			// Add a notice in the info panel if it exists
			const infoPanel = document.getElementById('byway-info');

			if (infoPanel) {
				const nearbyNotice = document.createElement('div');
				nearbyNotice.className = 'nearby-notice';
				nearbyNotice.innerHTML = `
					<h3>Nearby Byway Found!</h3>
					<p>You are about ${Math.round(minDistance)} km from <strong>${bywayName}</strong>.</p>
					<button id="view-nearby-byway" class="btn btn-primary">View this byway</button>
				`;

				infoPanel.prepend(nearbyNotice);

				// Add click handler
				document.getElementById('view-nearby-byway').addEventListener('click', () => {
					highlightByway(nearestByway);
				});
			} else {
				// If no info panel exists, show a temporary notification modal
				showNotificationModal(`Nearby Byway Found!`,
					`You are about ${Math.round(minDistance)} km from <strong>${bywayName}</strong>.`,
					nearestByway);
			}
		} catch (error) {
			console.error("Error displaying nearby byway notice:", error);
			// Show a temporary notification modal as fallback
			showNotificationModal(`Nearby Byway Found!`,
				`You are about ${Math.round(minDistance)} km from <strong>${bywayName}</strong>.`,
				nearestByway);
		}
	}
}

// Show a notification modal for nearby byways or other important messages
function showNotificationModal(title, message, bywayCode = null) {
	// Create modal element if it doesn't exist
	let modal = document.getElementById('notification-modal');
	if (!modal) {
		modal = document.createElement('div');
		modal.id = 'notification-modal';
		modal.className = 'modal notification-modal';
		document.body.appendChild(modal);
	}

	// Set content
	modal.innerHTML = `
		<div class="modal-content">
			<span class="modal-close">&times;</span>
			<h3>${title}</h3>
			<p class="modal-message">${message}</p>
			${bywayCode ? `<div class="modal-actions">
				<button class="view-byway-btn" data-code="${bywayCode}">View this byway</button>
			</div>` : ''}
		</div>
	`;

	// Show modal
	modal.classList.add('visible');

	// Add close button handler
	const closeBtn = modal.querySelector('.modal-close');
	if (closeBtn) {
		closeBtn.addEventListener('click', () => {
			modal.classList.remove('visible');
			setTimeout(() => {
				if (modal.parentNode) {
					modal.parentNode.removeChild(modal);
				}
			}, 300); // Wait for transition to complete
		});
	}

	// Add view byway button handler if bywayCode is provided
	if (bywayCode) {
		const viewBtn = modal.querySelector('.view-byway-btn');
		if (viewBtn) {
			viewBtn.addEventListener('click', () => {
				highlightByway(bywayCode);
				zoomToByway(bywayCode);
				// Get byway attributes and show details
				const bywayAttr = getBywayAttributesByCode(bywayCode);
				if (bywayAttr) {
					showBywayDetails(bywayAttr, getBywayFullName(bywayCode));
				}
				// Close modal after action
				modal.classList.remove('visible');
				setTimeout(() => {
					if (modal.parentNode) {
						modal.parentNode.removeChild(modal);
					}
				}, 300);
			});
		}
	}

	// Auto-hide after 10 seconds if not interacted with
	setTimeout(() => {
		if (modal && modal.classList.contains('visible')) {
			modal.classList.remove('visible');
			setTimeout(() => {
				if (modal && modal.parentNode) {
					modal.parentNode.removeChild(modal);
				}
			}, 300);
		}
	}, 10000);
}

// Calculate distance between two points in km using the Haversine formula
function calculateDistance(point1, point2) {
	const lat1 = point1.lat || point1[0];
	const lon1 = point1.lng || point1[1];
	const lat2 = point2.lat || point2[0];
	const lon2 = point2.lng || point2[1];

	const R = 6371; // Radius of the Earth in km
	const dLat = (lat2 - lat1) * Math.PI / 180;
	const dLon = (lon2 - lon1) * Math.PI / 180;

	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
		Math.sin(dLon / 2) * Math.sin(dLon / 2);

	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	const distance = R * c; // Distance in km

	return distance;
}

// Function to get byway attributes by code
function getBywayAttributesByCode(bywayCode) {
	// Find the first feature for this byway code
	const bywayFeature = bywaysData.find(feature =>
		feature.attributes && feature.attributes.SCENEHWY === bywayCode
	);

	return bywayFeature ? bywayFeature.attributes : null;
}

// Populate the byways list in the sidebar
function populateBywaysList() {
	const bywaysListElement = document.getElementById('byways-list-items');
	if (!bywaysListElement) return;

	// Clear existing list items
	bywaysListElement.innerHTML = '';

	// Get all unique byway codes and sort alphabetically by name
	const uniqueByways = Array.from(byways).map(code => ({
		code: code,
		name: getBywayFullName(code)
	})).sort((a, b) => a.name.localeCompare(b.name));

	// Add each byway to the list
	uniqueByways.forEach(byway => {
		const listItem = document.createElement('li');
		listItem.className = 'byway-list-item';
		listItem.dataset.code = byway.code;
		listItem.innerHTML = `
            <div class="byway-name">${byway.name}</div>
            <div class="byway-location">Florida Scenic Byway</div>
        `;

		// Add event listener to zoom to byway when clicked
		listItem.addEventListener('click', () => {
			// Save the byway name to the search history when clicked
			saveSearchTerm(byway.name);

			zoomToByway(byway.code);

			console.log(`Byway ${byway.code} clicked`);

			// Also show byway details in the panel
			const bywayAttr = getBywayAttributesByCode(byway.code);
			if (bywayAttr) {
				showBywayDetails(bywayAttr, byway.name);
			}

			// Close the popup after selection
			const popup = document.getElementById('byways-popup');
			popup.style.transform = getPopupSheetTransform(popup.offsetHeight);
			setTimeout(() => popup.classList.add('hidden'), 300);

			// Clear the search input when closing the panel
			clearSearch();
		});

		bywaysListElement.appendChild(listItem);
	});
}

// Function to reset any filtering applied to the byways list
function resetBywayFiltering() {
	// Clear the search input
	const searchInput = document.querySelector('.search-input');
	if (searchInput) {
		searchInput.value = '';
	}

	// Show all byway list items and remove highlighting
	const items = document.querySelectorAll('#byways-list-items li');
	items.forEach(item => {
		item.style.display = '';
		removeHighlighting(item);
	});

	// Remove any "no results" message
	const noResultsElem = document.getElementById('no-search-results');
	if (noResultsElem) noResultsElem.remove();
}

// Save search term to localStorage
function saveSearchTerm(term) {
	if (!term.trim()) return;

	const storedTerms = JSON.parse(localStorage.getItem('searchTerms')) || [];

	// Remove the term if it already exists (to avoid duplicates)
	const index = storedTerms.indexOf(term);
	if (index > -1) {
		storedTerms.splice(index, 1);
	}

	// Add the term to the beginning
	storedTerms.unshift(term);

	// Keep only the last 5 terms
	if (storedTerms.length > 5) {
		storedTerms.pop();
	}

	localStorage.setItem('searchTerms', JSON.stringify(storedTerms));
}

// Clear search input and dropdown
function clearSearch() {
	const searchInput = document.querySelector('.search-input');
	if (searchInput) {
		searchInput.value = '';
	}

	// Hide the dropdown if it exists
	hideSearchDropdown();
}

// Show dropdown with search history
function showSearchDropdown() {
	// Add a small delay to avoid focus conflicts
	setTimeout(() => {
		const searchInput = document.querySelector('.search-input');
		const searchContainer = searchInput.closest('.search-bar');

		// Add class to container when dropdown is open
		if (searchContainer) {
			searchContainer.classList.add('dropdown-open');
		}

		// Create dropdown if it doesn't exist
		let dropdown = document.getElementById('search-dropdown');
		if (!dropdown) {
			dropdown = document.createElement('ul');
			dropdown.id = 'search-dropdown';
			searchContainer.appendChild(dropdown);
		}

		// Get stored search terms
		const storedTerms = JSON.parse(localStorage.getItem('searchTerms')) || [];

		// Start building dropdown content
		dropdown.innerHTML = '';

		// First add the Byways button (appears at the top)
		const bywaysItem = document.createElement('li');
		bywaysItem.className = 'byways-item';
		bywaysItem.textContent = 'Byways';

		// Add click handler for byways button
		bywaysItem.addEventListener('click', (e) => {
			e.stopPropagation();
			hideSearchDropdown();
			const bywaysButton = document.querySelector('.category[data-category="byways"]');
			if (bywaysButton) {
				bywaysButton.click();
			}
		});

		dropdown.appendChild(bywaysItem);

		// If we have stored terms, add a divider and "More from recent history" section
		if (storedTerms.length > 0) {
			const divider = document.createElement('li');
			divider.className = 'divider';
			dropdown.appendChild(divider);

			const historyHeader = document.createElement('li');
			historyHeader.className = 'section-header';
			historyHeader.textContent = 'Recent history';
			dropdown.appendChild(historyHeader);

			// Add each stored term with a clock icon
			storedTerms.forEach(term => {
				const item = document.createElement('li');
				item.className = 'dropdown-item';
				item.textContent = term;

				// Replace the existing click handler with direct byway details display
				item.addEventListener('click', (e) => {
					e.stopPropagation();

					// Hide the dropdown immediately after click
					hideSearchDropdown();

					// Find the byway code that matches this search term
					const byway = Array.from(byways).find(code =>
						getBywayFullName(code).toLowerCase() === term.toLowerCase()
					);

					if (byway) {
						// Get byway attributes
						const bywayAttr = getBywayAttributesByCode(byway);
						if (bywayAttr) {
							// Directly show byway details and zoom to it
							showBywayDetails(bywayAttr, getBywayFullName(byway));
							zoomToByway(byway);
							highlightByway(byway);
						}
					} else {
						// Fallback to the original search behavior if no exact byway match
						searchInput.value = term;
						setTimeout(() => {
							handleSearch(term, true);
						}, 10);
					}
				});

				dropdown.appendChild(item);
			});
		}

		dropdown.classList.remove('hidden');
	}, 50); // Small delay to allow focus to settle
}

// Hide search dropdown
function hideSearchDropdown() {
	const dropdown = document.getElementById('search-dropdown');
	const searchBar = document.querySelector('.search-bar');

	if (searchBar) {
		searchBar.classList.remove('dropdown-open');
	}

	if (dropdown) {
		dropdown.classList.add('hidden');
	}
}

// Function to zoom to a specific byway
function zoomToByway(bywayCode) {
	try {
		// Find all features for this byway
		const bywayFeatures = bywaysData.filter(feature =>
			feature.attributes && feature.attributes.SCENEHWY === bywayCode
		);

		if (!bywayFeatures || bywayFeatures.length === 0) {
			console.error(`Byway with code ${bywayCode} not found`);
			return;
		}

		console.log(`Found ${bywayFeatures.length} segments for byway ${bywayCode}`);

		// Use the stored route layers instead of the undefined bywayLayer
		if (routeLayers[bywayCode] && routeLayers[bywayCode].length > 0) {
			// Create a bounds object to encompass all segments
			let bounds = null;

			routeLayers[bywayCode].forEach(layer => {
				const layerBounds = layer.getBounds();
				if (bounds === null) {
					bounds = layerBounds;
				} else {
					bounds.extend(layerBounds);
				}
			});

			if (bounds) {
				console.log("Before fitBounds - current zoom level:", map.getZoom());
				console.log("Applying fitBounds with bounds:", bounds.toBBoxString());
				map.fitBounds(bounds, {
					padding: [50, 50],
					maxZoom: 14
				});
				console.log("After fitBounds - new zoom level:", map.getZoom());
				console.log("Zoomed to byway using route layers");
			}
		} else if (bywayFeatures[0].geometry && bywayFeatures[0].geometry.paths) {
			// If we can't use layers, try to create bounds from geometry paths
			let allPoints = [];

			// Collect all path coordinates
			bywayFeatures.forEach(feature => {
				if (feature.geometry && feature.geometry.paths) {
					feature.geometry.paths.forEach(path => {
						path.forEach(coord => {
							// Convert ESRI format to Leaflet format (swap coordinates)
							allPoints.push([coord[1], coord[0]]);
						});
					});
				}
			});

			if (allPoints.length > 0) {
				// Create bounds from all collected points
				const bounds = L.latLngBounds(allPoints);
				map.fitBounds(bounds, {
					padding: [50, 50],
					maxZoom: 14
				});
				console.log("Zoomed to byway using geometry paths");
			}
		} else {
			console.error('Cannot zoom to byway: geometry not available');
		}

		// Highlight the byway segments
		highlightByway(bywayCode);

	} catch (error) {
		console.error('Error zooming to byway:', error);
	}
}

// Function to highlight a byway
function highlightByway(bywayCode) {
	// Highlight all segments of this byway
	if (routeLayers[bywayCode] && routeLayers[bywayCode].length > 0) {
		// First, reset all routes to normal appearance
		Object.keys(routeLayers).forEach(code => {
			routeLayers[code].forEach(layer => {
				layer.setStyle({
					weight: 2,
					opacity: 0.8
				});
			});
		});

		// Then highlight the selected byway
		routeLayers[bywayCode].forEach(layer => {
			layer.setStyle({
				weight: 4,
				opacity: 1
			});
			layer.bringToFront();
		});

		console.log(`Highlighted byway ${bywayCode}`);
	}
}

// Function to show byway details in the bottom panel
function showBywayDetails(attr, fullName) {
	const detailsPopup = document.getElementById('byway-details-popup');
	const detailsContent = document.getElementById('byway-details-content');

	// Generate the HTML content for the details
	detailsContent.innerHTML = createBywayDetailsContent(attr, fullName);

	// Show the popup
	detailsPopup.classList.remove('hidden');
	setTimeout(() => {
		detailsPopup.style.transform = getPopupSheetTransform(0);
	}, 10);

	// Add event listeners to the action buttons
	setupBywayDetailsEventListeners(attr.SCENEHWY);

	const bywayDetailsPopup = document.getElementById('byway-details-popup');
	bywayDetailsPopup.classList.remove('hidden');
	setTimeout(() => {
		bywayDetailsPopup.style.transform = getPopupSheetTransform(0);
	}, 10);

	// Dispatch event for schema markup generation
	if (window.dispatchEvent) {
		const bywayEvent = new CustomEvent('bywayDetailsDisplayed', {
			detail: {
				byway: {
					code: attr.SCENEHWY,
					name: fullName || getBywayFullName(attr.SCENEHWY),
					description: getBywayDescription(attr.SCENEHWY),
					county: attr.COUNTY
				}
			}
		});
		window.dispatchEvent(bywayEvent);
	}
}

// Function to create the content for the byway details panel
function createBywayDetailsContent(attr, fullName) {
	// Get the proper name and description from our mapping
	const bywayName = getBywayFullName(attr.SCENEHWY);
	const bywayDescription = getBywayDescription(attr.SCENEHWY);
	const bywayWebsite = getBywayWebsite(attr.SCENEHWY);

	// Create HTML content for the bottom panel
	let content = `
        <div class="byway-panel-content">
            <div class="byway-header-panel">
				<div class="byway-header-border"></div>
                <h3>${bywayName}</h3>
            </div>
            <div class="byway-details-panel">
                <p class="byway-description">${bywayDescription}</p>
                <div class="byway-info">
                    <div class="info-item">
                        <i class="fas fa-map-marker-alt"></i> ${attr.COUNTY} County
                    </div>`;

	if (attr.DESIG_DATE && attr.DESIG_DATE !== " ") {
		content += `
                    <div class="info-item">
                        <i class="fas fa-calendar-alt"></i> Designated: ${attr.DESIG_DATE}
                    </div>`;
	}

	if (bywayWebsite) {
		content += `
                    <div class="info-item">
                        <i class="fas fa-globe"></i>
                        <a href="${bywayWebsite}" target="_blank" rel="noopener">Official Website</a>
                    </div>`;
	}

	// Use best coordinate for navigation
	const coordinates = getBestNavigationCoordinate(attr.SCENEHWY);
	const [lat, lng] = coordinates;
	const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
	const appleMapsUrl = `https://maps.apple.com/?daddr=${lat},${lng}`;

	// Add navigation buttons before the image gallery
	content += `
                </div>
                <div class="byway-actions navigation-buttons">
                    <a href="${googleMapsUrl}" target="_blank" class="popup-action-btn nav-button google-maps" data-code="${attr.SCENEHWY}">
                        <i class="fab fa-google"></i> Navigate
                    </a>
                    <a href="${appleMapsUrl}" target="_blank" class="popup-action-btn nav-button apple-maps" data-code="${attr.SCENEHWY}">
                        <i class="fab fa-apple"></i> Navigate
                    </a>
                </div>`;

	// Now add the image gallery section after the navigation buttons
	content += `
                <div class="byway-images-container">
                    <div class="byway-images-scroll" id="byway-images-${attr.SCENEHWY}">
                        <!-- Images will be loaded dynamically -->
                        <div class="byway-image-loading">Loading images...</div>
                    </div>
                </div>
            </div>
        </div>`;

	return content;
}

// Function to set up event listeners for the byway details panel buttons
function setupBywayDetailsEventListeners(bywayCode) {
	// Handle the View Route button
	const viewBywayBtn = document.querySelector('.view-byway');
	if (viewBywayBtn) {
		viewBywayBtn.addEventListener('click', (e) => {
			e.stopPropagation();
			zoomToByway(bywayCode);
			highlightByway(bywayCode);
		});
	}

	// Load images for this byway
	loadBywayImages(bywayCode);
}

// Function to load images for a byway
function loadBywayImages(bywayCode) {
	const imagesContainer = document.getElementById(`byway-images-${bywayCode}`);
	if (!imagesContainer) return;

	// Clear loading message
	imagesContainer.innerHTML = '';

	// Hide the container by default - we'll show it only if images load successfully
	const parentContainer = imagesContainer.closest('.byway-images-container');
	if (parentContainer) {
		parentContainer.style.display = 'none';
	}

	// Use a single check image first to avoid multiple 404s
	const checkImage = new Image();
	checkImage.onload = function () {
		// If the check image loads, show the container and proceed with loading the rest
		if (parentContainer) {
			parentContainer.style.display = 'block';
		}

		// Create the grid container
		const gridContainer = document.createElement('div');
		gridContainer.className = 'byway-images-grid';
		imagesContainer.appendChild(gridContainer);

		// Now load all images knowing the folder exists
		loadAllBywayImages(bywayCode, gridContainer);
	};

	checkImage.onerror = function () {
		// If even the first image doesn't exist, don't try to load others
		console.log(`No images available for byway: ${bywayCode}`);
	};

	// Check if at least image 1 exists (jpg or png)
	checkImage.src = `images/${bywayCode}/1.jpg`;
}

// Helper function to load all images once we know the folder exists
function loadAllBywayImages(bywayCode, gridContainer) {
	const imagePath = `images/${bywayCode}/`;
	const maxImages = 6;
	let loadedImages = 0;

	for (let i = 1; i <= maxImages; i++) {
		// Try jpg first
		const img = new Image();

		img.onload = function () {
			const imageDiv = document.createElement('div');
			// First image and every third one should be large
			if (loadedImages % 3 === 0) {
				imageDiv.className = 'byway-image-item large';
			} else {
				imageDiv.className = 'byway-image-item small';
			}

			imageDiv.innerHTML = `<img src="${this.src}" alt="Image of ${getBywayFullName(bywayCode)}">`;
			gridContainer.appendChild(imageDiv);
			loadedImages++;
		};

		img.onerror = function () {
			// If jpg failed, try png
			if (this.src.endsWith('.jpg')) {
				this.src = `${imagePath}${i}.png`;
			}
		};

		// Start loading the jpg version
		img.src = `${imagePath}${i}.jpg`;
	}
}

// Set up event listeners for interactive elements
function setupEventListeners() {
	console.log("Setting up event listeners");

	// Handle Byways Popup
	const bywaysButton = document.querySelector('.category[data-category="byways"]');
	if (bywaysButton) {
		bywaysButton.addEventListener('click', () => {
			console.log("Byways button clicked");

			// Hide any open search dropdown before showing byways popup
			hideSearchDropdown();

			// Reset any filtering that was applied
			resetBywayFiltering();

			const popup = document.getElementById('byways-popup');
			popup.classList.remove('hidden');
			setTimeout(() => {
				popup.style.transform = getPopupSheetTransform(0);
			}, 10);

			const popupDetails = document.getElementById('byway-details-popup');
			popupDetails.style.transform = 'translateY(100%)';
			setTimeout(() => popupDetails.classList.add('hidden'), 300);
		});
	} else {
		console.error("Byways button not found");
	}

	// Close popup buttons
	const closePopupBtns = document.querySelectorAll('.close-popup');
	if (closePopupBtns.length) {
		closePopupBtns.forEach(btn => {
			btn.addEventListener('click', () => {
				const targetId = btn.dataset.target || 'byways-popup';
				const popup = document.getElementById(targetId);
				popup.style.transform = getPopupSheetTransform(popup.offsetHeight);
				setTimeout(() => popup.classList.add('hidden'), 300);

				// Clear search when popup is closed
				clearSearch();
			});
		});
	} else {
		console.error("Close popup buttons not found");
	}

	// Setup drag to close functionality for popup sheets
	setupPopupDragToClose();

	// Location button
	const locationButton = document.querySelector('.location-button');
	if (locationButton) {
		locationButton.addEventListener('click', () => {
			console.log("Location button clicked");
			getUserLocation();
		});
	} else {
		console.error("Location button not found");
	}

	// Search input functionality - enhanced to show dropdown with previous searches
	const searchInput = document.querySelector('.search-input');
	if (searchInput) {
		// Update search results as user types
		searchInput.addEventListener('input', (event) => {
			handleSearch(event.target.value);

			// Hide dropdown when typing
			hideSearchDropdown();
		});

		// Show dropdown when input is focused
		searchInput.addEventListener('focus', (e) => {
			// Only show dropdown if panel isn't open
			const popup = document.getElementById('byways-popup');

			if (popup.classList.contains('hidden')) {
				showSearchDropdown();
			} else {
				// If byways popup is open, close it and put focus back on search
				popup.style.transform = 'translateY(100%)';
				setTimeout(() => {
					popup.classList.add('hidden');
					// Re-focus and show dropdown after a short delay
					setTimeout(() => {
						searchInput.focus();
						showSearchDropdown();
					}, 50);
				}, 300);
			}
		});

		// Handle Enter key
		searchInput.addEventListener('keydown', (event) => {
			if (event.key === 'Enter' && event.target.value.trim() !== '') {
				handleSearch(event.target.value, true); // true = force focus on first match
			}
		});

		// Hide dropdown when clicking elsewhere
		document.addEventListener('click', (event) => {
			if (!event.target.closest('.search-input') && !event.target.closest('#search-dropdown')) {
				hideSearchDropdown();
			}
		});
	} else {
		console.error("Search input not found");
	}

	// Bottom nav items
	const navItems = document.querySelectorAll('.nav-item');
	if (navItems.length > 0) {
		navItems.forEach(item => {
			item.addEventListener('click', () => {
				console.log("Nav item clicked:", item.querySelector('span').textContent);

				// Remove active class from all items
				navItems.forEach(navItem => navItem.classList.remove('active'));

				// Add active class to clicked item
				item.classList.add('active');
			});
		});
	} else {
		console.error("Nav items not found");
	}

	// Close popup when clicking outside, but exclude map clicks
	document.addEventListener('click', (event) => {
		// Don't close panels when clicking on a route or the map
		if (event.target.classList.contains('leaflet-interactive') ||
			event.target.closest('#map') === map.getContainer()) {
			return;
		}

		const popups = document.querySelectorAll('.popup-sheet:not(.hidden)');
		popups.forEach(popup => {
			if (!event.target.closest('.popup-content') &&
				!event.target.closest('.category') &&
				!event.target.closest('.popup-header') &&
				!popup.contains(event.target)) {
				popup.style.transform = 'translateY(100%)';
				setTimeout(() => popup.classList.add('hidden'), 300);
			}
		});
	});

	// Add global keydown listener for Escape key
	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape') {
			// Close Byways Popup
			const bywaysPopup = document.getElementById('byways-popup');
			if (bywaysPopup && !bywaysPopup.classList.contains('hidden')) {
				bywaysPopup.style.transform = getPopupSheetTransform(bywaysPopup.offsetHeight);
				setTimeout(() => bywaysPopup.classList.add('hidden'), 300);
				clearSearch(); // Also clear search when closing via Esc
			}

			// Close Byway Details Popup
			const detailsPopup = document.getElementById('byway-details-popup');
			if (detailsPopup && !detailsPopup.classList.contains('hidden')) {
				detailsPopup.style.transform = getPopupSheetTransform(detailsPopup.offsetHeight);
				setTimeout(() => detailsPopup.classList.add('hidden'), 300);
			}

			// Close Search Dropdown
			hideSearchDropdown();
		}
	});

	console.log("Event listeners setup complete");

	// Setup voice search functionality
	setupVoiceSearch();
}

// Setup voice search functionality
function setupVoiceSearch() {
	const micButton = document.querySelector('.mic-icon');
	if (!micButton) return;

	// Check if browser supports speech recognition
	const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

	if (!SpeechRecognition) {
		console.warn("Speech recognition not supported in this browser");
		micButton.classList.add('disabled');
		micButton.title = "Voice search not supported in this browser";

		// Add click handler to show help modal
		micButton.addEventListener('click', () => {
			showVoiceSearchHelpModal("browser-not-supported");
		});
		return;
	}

	// Check if we're on HTTPS (required for speech recognition)
	if (window.location.protocol !== 'https:') {
		console.warn("Speech recognition requires HTTPS");
		micButton.classList.add('disabled');
		micButton.title = "Voice search requires HTTPS";

		// Add click handler to show help modal
		micButton.addEventListener('click', () => {
			showVoiceSearchHelpModal("https-required");
		});
		return;
	}

	const recognition = new SpeechRecognition();
	recognition.continuous = false;
	recognition.interimResults = false;
	recognition.lang = 'en-US';

	let isListening = false;
	let recognitionDisabled = false;

	// Add click event to mic button
	micButton.addEventListener('click', () => {
		if (recognitionDisabled) {
			showVoiceSearchHelpModal("permission-denied");
			return;
		}

		if (isListening) {
			recognition.stop();
		} else {
			try {
				recognition.start();
				isListening = true;

				// Update mic icon to show it's active
				micButton.classList.add('active');
			} catch (error) {
				console.error("Error starting speech recognition:", error);
				recognitionDisabled = true;
				micButton.classList.add('disabled');
				micButton.title = "Voice search not available";
				showVoiceSearchHelpModal("general-error");
			}
		}
	});

	// Handle speech recognition results
	recognition.onresult = (event) => {
		const transcript = event.results[0][0].transcript;
		console.log("Voice search result:", transcript);

		// Set the search input value and trigger search
		const searchInput = document.querySelector('.search-input');
		if (searchInput) {
			searchInput.value = transcript;

			// Trigger the input event to execute search
			searchInput.dispatchEvent(new Event('input'));

			// Focus on the search input
			searchInput.focus();
		}
	};

	// Handle when recognition ends (either from stop or auto-ending)
	recognition.onend = () => {
		isListening = false;
		micButton.classList.remove('active');
	};

	// Handle errors
	recognition.onerror = (event) => {
		console.error("Speech recognition error:", event.error);
		isListening = false;
		micButton.classList.remove('active');

		if (event.error === 'not-allowed') {
			recognitionDisabled = true;
			micButton.classList.add('disabled');
			micButton.title = "Microphone access denied";
		}
	};
}

// Show a helpful modal with instructions on how to enable voice search
function showVoiceSearchHelpModal(reason) {
	let title = "Voice Search Unavailable";
	let message = "";
	let instructions = "";

	switch (reason) {
		case "browser-not-supported":
			message = "Your browser doesn't support voice search.";
			instructions = `
                <p>To use voice search, please try:</p>
                <ul>
                    <li>Using Google Chrome, Microsoft Edge, or Safari</li>
                    <li>Updating your browser to the latest version</li>
                </ul>`;
			break;

		case "https-required":
			message = "Voice search requires a secure connection (HTTPS).";
			instructions = `
                <p>To use voice search:</p>
                <ul>
                    <li>Access this site using HTTPS</li>
                    <li>If you're running this locally, try using a local development server that supports HTTPS</li>
                </ul>`;
			break;

		case "permission-denied":
			message = "Microphone access was denied.";
			instructions = `
                <p>To enable voice search, you need to grant microphone permissions:</p>
                <ul>
                    <li>Click the lock/info icon in your browser's address bar</li>
                    <li>Find "Microphone" permissions and set to "Allow"</li>
                    <li>Reload the page</li>
                </ul>
                <p>Browser-specific instructions:</p>
                <details>
                    <summary>Chrome</summary>
                    <p>Click the lock icon in the address bar → Site Settings → Allow microphone</p>
                </details>
                <details>
                    <summary>Firefox</summary>
                    <p>Click the info icon (i) in the address bar → Permissions → Microphone → Allow</p>
                </details>
                <details>
                    <summary>Safari</summary>
                    <p>Go to Preferences → Websites → Microphone → Allow for this website</p>
                </details>`;
			break;

		case "general-error":
		default:
			message = "There was a problem activating voice search.";
			instructions = `
                <p>Please try the following:</p>
                <ul>
                    <li>Make sure your microphone is connected and working</li>
                    <li>Grant microphone permissions in your browser settings</li>
                    <li>Refresh the page and try again</li>
                </ul>`;
			break;
	}

	// Create modal element if it doesn't exist
	let modal = document.getElementById('voice-help-modal');
	if (!modal) {
		modal = document.createElement('div');
		modal.id = 'voice-help-modal';
		modal.className = 'modal help-modal';
		document.body.appendChild(modal);
	}

	// Set content
	modal.innerHTML = `
        <div class="modal-content">
            <span class="modal-close">&times;</span>
            <h3>${title}</h3>
            <p class="modal-message">${message}</p>
            <div class="modal-instructions">${instructions}</div>
        </div>
    `;

	// Show modal
	modal.classList.add('visible');

	// Add close button handler
	const closeBtn = modal.querySelector('.modal-close');
	if (closeBtn) {
		closeBtn.addEventListener('click', () => {
			modal.classList.remove('visible');
			setTimeout(() => {
				if (modal.parentNode) {
					modal.parentNode.removeChild(modal);
				}
			}, 300); // Wait for transition to complete
		});
	}
}

// Helper: Find the best navigation coordinate for a byway (closest to user, or first)
function getBestNavigationCoordinate(bywayCode) {
	if (!routeMarkers[bywayCode] || routeMarkers[bywayCode].length === 0) {
		return [0, 0];
	}
	if (userLocation) {
		let minDist = Infinity;
		let best = null;
		routeMarkers[bywayCode].forEach(seg => {
			const dStart = calculateDistance(userLocation, seg.start);
			const dEnd = calculateDistance(userLocation, seg.end);
			if (dStart < minDist) {
				minDist = dStart;
				best = seg.start;
			}
			if (dEnd < minDist) {
				minDist = dEnd;
				best = seg.end;
			}
		});
		return best;
	}
	// fallback: first segment's start
	return routeMarkers[bywayCode][0].start;
}

// Utility: get correct transform for popup-sheet based on viewport width
function getPopupSheetTransform(yPx = 0) {
	if (window.innerWidth >= 768) {
		return `translateX(-50%) translateY(${yPx}px)`;
	} else {
		return `translateY(${yPx}px)`;
	}
}

// Setup drag-to-close functionality for popup sheets
function setupPopupDragToClose() {
	const popupSheets = document.querySelectorAll('.popup-sheet');

	popupSheets.forEach(popup => {
		const header = popup.querySelector('.popup-header');
		if (!header) return;

		let startY, currentY;
		let isDragging = false;

		// Touch/mouse start
		header.addEventListener('touchstart', (e) => {
			startY = e.touches[0].clientY;
			isDragging = true;
		}, { passive: true });

		header.addEventListener('mousedown', (e) => {
			startY = e.clientY;
			isDragging = true;
		});

		// Touch/mouse move
		document.addEventListener('touchmove', (e) => {
			if (!isDragging) return;

			currentY = e.touches[0].clientY;
			const deltaY = currentY - startY;

			if (deltaY > 0) { // Only allow downward dragging
				popup.style.transform = getPopupSheetTransform(deltaY);
				e.preventDefault(); // Prevent scrolling while dragging
			}
		}, { passive: false });

		document.addEventListener('mousemove', (e) => {
			if (!isDragging) return;

			currentY = e.clientY;
			const deltaY = currentY - startY;

			if (deltaY > 0) { // Only allow downward dragging
				popup.style.transform = getPopupSheetTransform(deltaY);
			}
		});

		// Touch/mouse end
		const endDrag = () => {
			if (!isDragging) return;
			isDragging = false;

			if (!currentY) return;

			const deltaY = currentY - startY;

			// If dragged more than 50px or 15% of height, close the popup
			if (deltaY > 50 || deltaY > (popup.offsetHeight * 0.15)) {
				popup.style.transform = getPopupSheetTransform(popup.offsetHeight);
				setTimeout(() => popup.classList.add('hidden'), 300);
			} else {
				// Otherwise, snap back
				popup.style.transform = getPopupSheetTransform(0);
			}
		};

		document.addEventListener('touchend', endDrag);
		document.addEventListener('mouseup', endDrag);
	});
}

// Function to handle search queries
function handleSearch(query, focusOnFirstMatch = false) {
	const searchQuery = query.toLowerCase().trim();
	const popup = document.getElementById('byways-popup');
	const items = document.querySelectorAll('#byways-list-items li');

	// Only proceed if we have a query and items to search
	if (items.length === 0) return;

	// Display the byways popup when search is used
	if (searchQuery.length >= 1 && popup.classList.contains('hidden')) {
		// Open the popup as soon as user starts typing
		popup.classList.remove('hidden');
		setTimeout(() => {
			popup.style.transform = getPopupSheetTransform(0);
		}, 10);
	}

	// Variables to track matches
	let hasMatches = false;
	let firstMatch = null;

	// Filter and highlight matches
	items.forEach(item => {
		// Search in entire item text content
		const text = item.textContent.toLowerCase();
		const matches = text.includes(searchQuery);

		if (matches) {
			hasMatches = true;
			if (!firstMatch) firstMatch = item;

			// Show item and highlight the match
			item.style.display = '';
			highlightSearchMatch(item, searchQuery);
		} else {
			// Hide non-matching items
			item.style.display = 'none';
			removeHighlighting(item);
		}
	});

	// Update UI based on search results
	if (searchQuery.length > 0) {
		// If we have no matches and the search box isn't empty, show "no results"
		const noResultsElem = document.getElementById('no-search-results');

		if (!hasMatches) {
			// Create "no results" message if it doesn't exist
			if (!noResultsElem) {
				const noResults = document.createElement('div');
				noResults.id = 'no-search-results';
				noResults.className = 'no-results-message';
				noResults.textContent = `No byways found matching "${searchQuery}"`;
				popup.querySelector('.popup-content').appendChild(noResults);
			}
		} else if (noResultsElem) {
			// Remove "no results" message if it exists and we have results
			noResultsElem.remove();
		}
	} else {
		// If search is empty, show all items without highlighting
		items.forEach(item => {
			item.style.display = '';
			removeHighlighting(item);
		});

		// Remove any "no results" message
		const noResultsElem = document.getElementById('no-search-results');
		if (noResultsElem) noResultsElem.remove();
	}

	// Focus on the first match if requested
	if (focusOnFirstMatch && firstMatch) {
		firstMatch.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		// Add a temporary pulse animation to draw attention
		firstMatch.classList.add('search-match-pulse');
		setTimeout(() => {
			firstMatch.classList.remove('search-match-pulse');
		}, 1500);
	}
}

// Helper function to highlight the search match in an item
function highlightSearchMatch(listItem, searchQuery) {
	// We only want to highlight the byway name, not the location text
	const nameElement = listItem.querySelector('.byway-name');
	if (!nameElement) return;

	// Get the original text if we haven't stored it yet
	if (!nameElement.dataset.originalText) {
		nameElement.dataset.originalText = nameElement.textContent;
	}

	const originalText = nameElement.dataset.originalText;
	const lowerOriginal = originalText.toLowerCase();

	// Check if the search query is in the text
	if (lowerOriginal.includes(searchQuery)) {
		// Find the start position of the match
		const startPos = lowerOriginal.indexOf(searchQuery);
		const endPos = startPos + searchQuery.length;

		// Create the new HTML with highlighted part
		const beforeMatch = originalText.substring(0, startPos);
		const match = originalText.substring(startPos, endPos);
		const afterMatch = originalText.substring(endPos);

		// Set the HTML with highlight
		nameElement.innerHTML = `${beforeMatch}<span class="search-highlight">${match}</span>${afterMatch}`;

		// Add highlight class to the list item
		listItem.classList.add('has-search-highlight');
	}
}

// Helper function to remove highlighting
function removeHighlighting(listItem) {
	const nameElement = listItem.querySelector('.byway-name');
	if (!nameElement) return;

	// Restore original text if we have it stored
	if (nameElement.dataset.originalText) {
		nameElement.textContent = nameElement.dataset.originalText;
	}

	// Remove highlight class
	listItem.classList.remove('has-search-highlight');
}

// helper: display modal with close button + auto‑fade
function showErrorModal(message) {
	// remove any existing
	const existing = document.getElementById('error-modal');
	if (existing) existing.remove();

	const modal = document.createElement('div');
	modal.id = 'error-modal';
	modal.className = 'modal';

	const content = document.createElement('div');
	content.className = 'modal-content';
	content.innerHTML = `<p>${message}</p>`;

	const closeBtn = document.createElement('span');
	closeBtn.className = 'modal-close';
	closeBtn.innerHTML = '&times;';
	closeBtn.addEventListener('click', () => fadeAndRemove(modal));

	content.appendChild(closeBtn);
	modal.appendChild(content);
	document.body.appendChild(modal);

	// auto‑fade after 10s
	setTimeout(() => fadeAndRemove(modal), 10000);
}

function fadeAndRemove(modal) {
	modal.classList.add('fade-out');
	modal.addEventListener('transitionend', () => {
		if (modal.parentNode) modal.parentNode.removeChild(modal);
	});
}

// Now that all functions are defined, we can safely call the initialization code
// Attempt to get user location immediately on page load
getUserLocation();

// Initialize the application
fetchData();
